import { formatStoreMoney, isZeroMoney } from "@/lib/formatting/money";
import type { CartAddress, CheckoutShippingRate } from "@/types/cart";
import { ShippingArrival } from "./ShippingArrival";
import { ShippingBadgeLabel } from "@/components/UI/ShippingBadgeLabel";
import type { ShippingBadge } from "@/lib/shipping/shippingBadges";

function formatDeliveryTime(value: string | undefined): string | undefined {
  const normalized = value?.trim();
  if (!normalized || !/^\d+$/.test(normalized)) return normalized;

  const days = Number(normalized);
  return `${days} ${days === 1 ? "dia útil" : "dias úteis"}`;
}

interface ShippingOptionCardProps {
  checked: boolean;
  disabled: boolean;
  groupName: string;
  onSelect: () => void;
  rate: CheckoutShippingRate;
  /** Para a previsão de chegada: CEP, cidade e UF do destino do pacote. */
  destination?: CartAddress;
  selectable?: boolean;
  /** Selo "Mais barato" / "Mais rápido" (só visual). */
  badge?: ShippingBadge;
  /** Linha enxuta, sem cartão (página do carrinho). */
  compact?: boolean;
}

export function ShippingOptionCard({
  checked,
  disabled,
  groupName,
  onSelect,
  rate,
  destination,
  selectable = true,
  badge,
  compact = false,
}: ShippingOptionCardProps) {
  const descriptionId = `${groupName}-${rate.rateId.replace(/[^a-z0-9_-]/gi, "-")}-description`;
  const detail =
    formatDeliveryTime(rate.deliveryTime) ||
    rate.description ||
    (rate.methodId === "local_pickup"
      ? "Retirada na loja após confirmação."
      : "");

  if (compact) {
    const free = isZeroMoney(rate.price);
    const row = (
      <>
        {selectable ? (
          <input
            type="radio"
            name={groupName}
            value={rate.rateId}
            checked={checked}
            disabled={disabled}
            onChange={onSelect}
            aria-describedby={detail ? descriptionId : undefined}
            className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
          />
        ) : null}
        <span className="min-w-0 flex-1 text-xs">
          <span className="flex items-baseline justify-between gap-3">
            <span className="flex min-w-0 flex-wrap items-center gap-1.5">
              <strong className="text-foreground">{rate.name}</strong>
              <ShippingBadgeLabel badge={badge} />
            </span>
            <strong className={free ? "shrink-0 text-emerald-700" : "shrink-0 text-secondary"}>
              {free ? "Grátis" : formatStoreMoney(rate.price)}
            </strong>
          </span>
          {detail ? (
            <span id={descriptionId} className="mt-0.5 block truncate text-[11px] text-muted" title={detail}>
              {detail}
            </span>
          ) : null}
          <ShippingArrival
            rate={rate}
            destination={{ postcode: destination?.postcode, city: destination?.city, uf: destination?.state }}
            className="block text-[11px] font-medium text-emerald-700"
          />
        </span>
      </>
    );
    return selectable ? (
      <label className="flex cursor-pointer items-start gap-2 py-2 has-[:checked]:[&_strong]:text-heading">{row}</label>
    ) : (
      <div role="listitem" className="flex items-start gap-2 py-2">{row}</div>
    );
  }

  const content = (
    <>
      {selectable ? (
      <input
        type="radio"
        name={groupName}
        value={rate.rateId}
        checked={checked}
        disabled={disabled}
        onChange={onSelect}
        aria-describedby={detail ? descriptionId : undefined}
        className="mt-0.5 h-5 w-5 shrink-0 accent-primary"
      />
      ) : null}
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap justify-between gap-x-4 gap-y-1">
          <span className="flex flex-wrap items-center gap-2">
            <strong className="text-sm text-foreground">{rate.name}</strong>
            <ShippingBadgeLabel badge={badge} />
          </span>
          <strong className="text-sm text-foreground">
            {isZeroMoney(rate.price)
              ? "Grátis"
              : formatStoreMoney(rate.price)}
          </strong>
        </span>
        {detail ? (
          <span
            id={descriptionId}
            className="mt-1 block text-sm leading-5 text-muted"
          >
            {detail}
          </span>
        ) : null}
        <ShippingArrival
          rate={rate}
          destination={{
            postcode: destination?.postcode,
            city: destination?.city,
            uf: destination?.state,
          }}
        />
      </span>
    </>
  );

  return selectable ? (
    <label className="flex min-h-16 cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 transition duration-200 has-[:checked]:border-primary has-[:checked]:bg-blue-50/60">
      {content}
    </label>
  ) : (
    <div
      role="listitem"
      className="flex min-h-16 items-start gap-3 rounded-xl border border-slate-200 bg-white p-4"
    >
      {content}
    </div>
  );
}
